import React, { useEffect, useRef } from 'react';
import styles from './StarField.module.css';

export default function StarField({ density = 100, speed = 0.5 }) {
  const canvasRef = useRef(null);
  const starsRef = useRef([]);
  const meteorsRef = useRef([]);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initStars();
      initMeteors();
    };

    const initStars = () => {
      starsRef.current = Array.from({ length: density }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 1.8 + 0.5,
        opacity: Math.random() * 0.8 + 0.2,
        twinkleSpeed: Math.random() * 0.02 + 0.005,
        twinklePhase: Math.random() * Math.PI * 2,
      }));
    };

    const initMeteors = () => {
      // 减少流星数量到2个
      meteorsRef.current = Array.from({ length: 2 }, () => createMeteor(canvas));
    };

    const createMeteor = (canvas) => ({
      x: Math.random() * canvas.width * 1.5,
      y: -100 - Math.random() * 300,
      length: Math.random() * 60 + 40,
      speed: Math.random() * 6 + 8,
      opacity: 0,
      fadeIn: true,
      // 大幅增加延迟，让流星出现更稀少（8-18秒间隔）
      delay: Math.random() * 600 + 500,
    });

    const animate = () => {
      // 增加背景清除的透明度，让拖尾消失更快
      ctx.fillStyle = 'rgba(10, 10, 15, 0.25)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 绘制星星
      starsRef.current.forEach((star) => {
        star.twinklePhase += star.twinkleSpeed;
        const twinkle = Math.sin(star.twinklePhase) * 0.3 + 0.7;
        
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(232, 232, 240, ${star.opacity * twinkle})`;
        ctx.fill();

        star.y += speed * 0.05;
        if (star.y > canvas.height) {
          star.y = 0;
          star.x = Math.random() * canvas.width;
        }
      });

      // 绘制流星
      meteorsRef.current.forEach((meteor, index) => {
        if (meteor.delay > 0) {
          meteor.delay--;
          return;
        }

        // 淡入
        if (meteor.fadeIn) {
          meteor.opacity += 0.08;
          if (meteor.opacity >= 1) meteor.fadeIn = false;
        }

        // 移动流星（从右上往左下斜着划）
        meteor.x -= meteor.speed * 0.7;
        meteor.y += meteor.speed;

        // 绘制流星尾巴 - 更短更细的尾巴
        const gradient = ctx.createLinearGradient(
          meteor.x, meteor.y,
          meteor.x + meteor.length * 0.6, meteor.y - meteor.length * 0.8
        );
        gradient.addColorStop(0, `rgba(255, 255, 255, ${meteor.opacity})`);
        gradient.addColorStop(0.2, `rgba(220, 220, 240, ${meteor.opacity * 0.5})`);
        gradient.addColorStop(1, 'rgba(200, 200, 220, 0)');

        ctx.beginPath();
        ctx.moveTo(meteor.x, meteor.y);
        ctx.lineTo(meteor.x + meteor.length * 0.6, meteor.y - meteor.length * 0.8);
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // 流星头部光点
        ctx.beginPath();
        ctx.arc(meteor.x, meteor.y, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${meteor.opacity})`;
        ctx.fill();

        // 重置流星
        if (meteor.y > canvas.height + 100 || meteor.x < -100) {
          meteorsRef.current[index] = createMeteor(canvas);
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    resize();
    animate();
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationRef.current);
    };
  }, [density, speed]);

  return <canvas ref={canvasRef} className={styles.canvas} />;
}
